import React from 'react';

/**
 * Loading Skeleton Component
 * 
 * Placeholder animations for loading states
 */

const Skeleton = ({ className = '', variant = 'rectangular' }) => {
  const baseStyles = 'animate-pulse bg-gray-200 rounded';
  
  const variants = {
    rectangular: 'rounded-lg',
    circular: 'rounded-full',
    text: 'rounded h-4',
  };

  return (
    <div className={`${baseStyles} ${variants[variant]} ${className}`} />
  );
};

// Pre-built skeleton components for common patterns
export const TextSkeleton = ({ lines = 3, className = '' }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton 
        key={i} 
        variant="text" 
        className={i === lines - 1 ? 'w-3/4' : 'w-full'}
      />
    ))}
  </div>
);

export const CardSkeleton = ({ className = '' }) => (
  <div className={`bg-white rounded-xl p-6 shadow-md ${className}`}>
    <div className="flex items-start gap-4">
      <Skeleton variant="circular" className="w-12 h-12 flex-shrink-0" />
      <div className="flex-1 space-y-3">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  </div>
);

export const ChatCardSkeleton = ({ className = '' }) => (
  <div className={`bg-white rounded-xl p-6 shadow-md ${className}`}>
    <div className="flex items-start justify-between">
      <div className="flex-1 space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton variant="circular" className="w-8 h-8" />
          <Skeleton className="h-5 w-24" />
        </div>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <div className="flex gap-4 pt-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
      <Skeleton className="h-10 w-28 rounded-lg" />
    </div>
  </div>
);

export const MessageSkeleton = ({ isUser = false, className = '' }) => (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${className}`}>
    <div className={`max-w-[80%] ${isUser ? 'bg-blue-100' : 'bg-gray-100'} rounded-lg p-4 space-y-2`}>
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-4 w-32" />
    </div>
  </div>
);

export const PricingCardSkeleton = ({ className = '' }) => (
  <div className={`bg-white rounded-2xl p-8 border shadow-md ${className}`}>
    <Skeleton className="h-6 w-20 mb-4" />
    <Skeleton className="h-10 w-32 mb-6" />
    <div className="space-y-3 mb-8">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <Skeleton variant="circular" className="w-5 h-5" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
    <Skeleton className="h-12 w-full rounded-lg" />
  </div>
);

export const TableRowSkeleton = ({ columns = 4, className = '' }) => (
  <div className={`flex gap-4 p-4 border-b ${className}`}>
    {Array.from({ length: columns }).map((_, i) => (
      <Skeleton key={i} className="h-4 flex-1" />
    ))}
  </div>
);

export const AvatarSkeleton = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24',
  };

  return <Skeleton variant="circular" className={`${sizes[size]} ${className}`} />;
};

export default Skeleton;
